import React, { createContext, useContext, useEffect, useState } from 'react'

import {
  DeliveryInfoInput,
  SavedAddress,
  useValidateDeliveryDistanceLazyQuery,
  ValidateDeliveryDistanceDocument,
  ValidateDeliveryDistanceQueryResult
} from 'src/apollo/onlineOrdering'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'

import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'

import { useCustomer } from './CustomerContextCommon'
import { DeliveryAddressScreen } from '../default_template/online_ordering/delivery_address_modal/DeliveryModal'

import { reportErrorMessageWithData } from 'src/lib/js/clientError'

export type RangedCustomerAddress = SavedAddress & { inRangeOfRx: boolean }

export type DeliveryContextType = {
  isAddressInRange: (address: DeliveryInfoInput) => Promise<boolean | undefined>
  savedAddressUsed: boolean // is the current delivery address a saved address
  setSavedAddressUsed: (savedAddressUsed: boolean) => void
  deliveryModalOpen: boolean
  setDeliveryModalOpen: (
    open: boolean,
    forceScreenPlacement?: DeliveryAddressScreen
  ) => void
  deliveryModalForcedScreen: DeliveryAddressScreen | undefined
  customerAddresses: RangedCustomerAddress[] // pre-validated saved addresses allowing for optimistic UI updates
  lastKnownValidDeliveryAddress: DeliveryInfoInput | null // The most recently confirmed delivery address, persists through toggling to pickup
  setLastKnownValidDeliveryAddress: (address: DeliveryInfoInput | null) => void
  saveSessionAddress: boolean // should the current delivery address be saved to the customer account
  setSaveSessionAddress: (save: boolean) => void
  sessionAddressLabel: string // if `saveSessionAddress` is true, the label for this address to save
  setSessionAddressLabel: (label: string) => void
}

export const DeliveryContext = createContext<DeliveryContextType | undefined>(
  undefined
)

export const DeliveryContextProvider = (props: React.PropsWithChildren<{}>) => {
  const { selectedLocation } = useRestaurant()
  const { savedAddressUsed, setSavedAddressUsed, customer } = useCustomer()
  // customer addresses with indication of whether or not they are in range of the current session's restaurant.
  const [customerAddresses, setCustomerAddresses] = useState<
    RangedCustomerAddress[]
  >([])

  // New state for fulfillment redesign experiment
  const [lastKnownValidDeliveryAddress, setLastKnownValidDeliveryAddress] =
    useState<DeliveryInfoInput | null>(null)
  const [saveSessionAddress, setSaveSessionAddress] = useState(false)
  const [sessionAddressLabel, setSessionAddressLabel] = useState('')

  const [deliveryModalOpenState, setDeliveryModalOpenState] = useState(false)
  const [deliveryModalForcedScreen, setDeliveryModalForcedScreen] = useState<
    DeliveryAddressScreen | undefined
  >(undefined)
  const client = useOOClient()

  const [validateDeliveryDistance] = useValidateDeliveryDistanceLazyQuery({
    fetchPolicy: 'no-cache',
    client
  })

  const isAddressInRange = async (address: DeliveryInfoInput) => {
    const response = await validateDeliveryDistance({
      variables: {
        input: {
          deliveryInfo: address,
          restaurantGuid: selectedLocation.externalId
        }
      }
    })
    return response.data?.validateDeliveryLocationV2.valid
  }

  // Automatically validate and update potentially valid saved addresses when customer or location changes
  useEffect(() => {
    const validateCustomerSavedAddresses = async () => {
      if (
        !customer?.addresses ||
        customer.addresses.length === 0 ||
        !selectedLocation.externalId
      ) {
        setCustomerAddresses([])
        return
      }

      const validationPromises = customer.addresses.map(async (address) => {
        const deliveryInfoInput = {
          ...address.deliveryInfo,
          __typename: undefined
        }
        if (!deliveryInfoInput) {
          return { ...address, inRangeOfRx: false }
        }

        try {
          const { data } = await client.query<
            ValidateDeliveryDistanceQueryResult['data']
          >({
            query: ValidateDeliveryDistanceDocument,
            variables: {
              input: {
                deliveryInfo: deliveryInfoInput,
                restaurantGuid: selectedLocation.externalId
              }
            },
            fetchPolicy: 'no-cache'
          })
          return {
            ...address,
            inRangeOfRx: data?.validateDeliveryLocationV2.valid || false
          }
        } catch (error) {
          reportErrorMessageWithData('Failed to validate delivery location', {
            savedAddress: true,
            input: deliveryInfoInput
          })
          return { ...address, inRangeOfRx: false }
        }
      })

      const validatedCustomerAddresses = await Promise.all(validationPromises)
      setCustomerAddresses(validatedCustomerAddresses)
    }

    validateCustomerSavedAddresses()
  }, [customer, selectedLocation.externalId, client])

  return (
    <DeliveryContext.Provider
      value={{
        isAddressInRange,
        savedAddressUsed,
        setSavedAddressUsed,
        deliveryModalOpen: deliveryModalOpenState,
        setDeliveryModalOpen: (
          open: boolean,
          forceScreenPlacement?: DeliveryAddressScreen
        ) => {
          setDeliveryModalOpenState(open)
          setDeliveryModalForcedScreen(forceScreenPlacement)
        },
        deliveryModalForcedScreen,
        customerAddresses,
        lastKnownValidDeliveryAddress,
        setLastKnownValidDeliveryAddress,
        saveSessionAddress,
        setSaveSessionAddress,
        sessionAddressLabel,
        setSessionAddressLabel
      }}
    >
      {props.children}
    </DeliveryContext.Provider>
  )
}

export const useDelivery = () => {
  const context = useContext(DeliveryContext)
  if (!context) {
    throw new Error('useDelivery must be used within a DeliveryContextProvider')
  }

  return context
}
